import React from 'react'
import {ColDef, GridApi, GridOptions} from 'ag-grid-community'
import {OcTransferListGridProps} from './TransferListGrid'
import {ActionsCellRenderer} from './TransferGridActionCellRenderer'
import {OcTransferListProps} from './TransferList'

export enum OcTransferDirection {
    Left,
    Right
}

export interface OcTransferListTranslations {
    transferLeft?: string
    transferRight?: string
    moveUp?: string
    moveDown?: string
}

const dragAndDropColumnId = 'dragAndDrop'
const actionsColumnId = 'actions'
const notClickableCells = new Set([dragAndDropColumnId, actionsColumnId])
const iconWidth = 30

export function useColumnDefs<T>(
    globalProps: OcTransferListProps<T>,
    gridProps: OcTransferListGridProps<T>,
    transferDirection: OcTransferDirection
): ColDef<T>[] {
    const orderableWithButtons =
        (gridProps.orderableWithButtons ?? globalProps.orderableWithRowButtons) && !!gridProps.move
    const orderableWithDragAndDrop =
        (gridProps.orderableWithDragAndDrop ?? globalProps.orderableWithDragAndDrop) && !!gridProps.move
    const actionColumnWidth =
        iconWidth *
            ((!globalProps.disabled && globalProps.transferWithRowButtons ? 1 : 0) +
                (!globalProps.disabled && orderableWithButtons ? 2 : 0)) +
        12

    const indexOf = React.useCallback((item: T) => gridProps.data.indexOf(item), [gridProps.data])

    return React.useMemo<ColDef<T>[]>(
        () => [
            ...(!globalProps.disabled && orderableWithDragAndDrop
                ? [
                      {
                          colId: dragAndDropColumnId,
                          rowDrag: true,
                          resizable: false,
                          sortable: false,
                          filter: false,
                          width: 32,
                          minWidth: 32,
                          maxWidth: 32
                      }
                  ]
                : []),
            ...(!globalProps.disabled && (orderableWithButtons || globalProps.transferWithRowButtons)
                ? [
                      {
                          colId: actionsColumnId,
                          cellRenderer: ActionsCellRenderer,
                          cellRendererParams: {
                              transferDirection,
                              indexOf,
                              maxIndex: gridProps.data.length,
                              transfer:
                                  !globalProps.disabled && globalProps.transferWithRowButtons && gridProps.transfer,
                              move: !globalProps.disabled && orderableWithButtons && gridProps.move,
                              translations: globalProps.translations
                          },
                          resizable: false,
                          sortable: false,
                          filter: false,
                          width: actionColumnWidth,
                          minWidth: actionColumnWidth,
                          maxWidth: actionColumnWidth,
                          cellStyle: {
                              padding: '0px 0px 0px 12px'
                          }
                      }
                  ]
                : []),
            ...(gridProps.columnDefs ?? globalProps.gridOptions?.columnDefs ?? globalProps.columnDefs ?? [])
                .filter((x): x is ColDef<T> => !('children' in x))
                .map(colDef => ({
                    ...colDef,
                    cellStyle: {
                        ...colDef.cellStyle,
                        ...(!globalProps.disabled && globalProps.transferWithRowClick ? {cursor: 'pointer'} : {})
                    }
                }))
        ],
        [
            globalProps.columnDefs,
            globalProps.gridOptions?.columnDefs,
            globalProps.transferWithRowButtons,
            globalProps.transferWithRowClick,
            globalProps.translations,
            globalProps.disabled,
            gridProps.columnDefs,
            gridProps.data,
            gridProps.transfer,
            gridProps.move,
            indexOf,
            transferDirection,
            orderableWithButtons,
            orderableWithDragAndDrop,
            actionColumnWidth
        ]
    )
}

export function useGridOptions<T>(
    globalProps: OcTransferListProps<T>,
    gridProps: OcTransferListGridProps<T>,
    storeGridApi: (api: GridApi<T>) => void
): GridOptions<T> {
    const transfer = gridProps.transfer
    const move = gridProps.move
    const orderableWithDragAndDrop =
        (gridProps.orderableWithDragAndDrop ?? globalProps.orderableWithDragAndDrop) && !!gridProps.move

    return React.useMemo<GridOptions<T>>(
        () => ({
            ...globalProps.gridOptions,
            ...gridProps.gridOptions,
            ...(!globalProps.disabled && globalProps.transferWithRowClick
                ? {
                      onCellClicked: cell => {
                          if (cell.colDef.colId && !notClickableCells.has(cell.colDef.colId) && cell.data) {
                              transfer(cell.data)
                          }
                      }
                  }
                : {}),
            ...(!globalProps.disabled && orderableWithDragAndDrop
                ? {
                      rowDragManaged: true,
                      onRowDragEnd: event => (event.node.data ? move?.(event.node.data, event.overIndex) : undefined)
                  }
                : {}),
            ...(!globalProps.disabled && globalProps.transferWithSharedButtons
                ? {
                      rowSelection: {
                          mode: 'multiRow',
                          checkboxes: false,
                          enableClickSelection: true,
                          headerCheckbox: false
                      }
                  }
                : {}),
            onGridReady: event => {
                ;(gridProps.gridOptions?.onGridReady ?? globalProps.gridOptions?.onGridReady)?.(event)
                storeGridApi(event.api)
            }
        }),
        [
            globalProps.gridOptions,
            globalProps.transferWithRowClick,
            globalProps.transferWithSharedButtons,
            gridProps.gridOptions,
            globalProps.disabled,
            orderableWithDragAndDrop,
            transfer,
            move,
            storeGridApi
        ]
    )
}
